import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  copyToClipboard,
  formatDate,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminParticipantsQueryKey,
  useDeletePlan,
  useGetAdminAdvisoryOrganization,
  useGetAdminPlanEquityWashRules,
  useGetAdminPlanManagedAccountProgram,
  useGetPlanSuspense,
  usePatchAdminPlanStatus,
  usePatchPlan,
  usePostAdminParticipantLiteSync,
  usePostAdminPlanMapReanalysis,
  usePostAdminPlanReliusCheckIJoinEnabled,
  usePostAdminPlanReliusParticipantSync,
  usePostAdminPlanReliusPlanSync,
  usePostAdminSsnResetAll
} from '@ijoin/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate
} from '@tanstack/react-router';

import { useToggle } from 'usehooks-ts';

import { QueryBoundary } from '../../../../components';

const PLAN_STATUS = {
  ARCHIVE: 'ea6ff457-3b8c-4c70-ba70-566b96178c87',
  DELETING: 'a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d',
  LIVE: '5c1a4b9b-f33e-4038-bb86-b0710ae74887'
};

const PLAN_STATUS_LABELS: Record<string, string> = {
  '5c1a4b9b-f33e-4038-bb86-b0710ae74887': 'Live',
  'a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d': 'Deleting',
  'a30120e9-8b0a-4b94-8686-01a2d849724c': 'Draft (Vehicles Locked)',
  'af67a5c2-939e-40db-8b1b-686fd66b4879': 'Draft (New)',
  'ea6ff457-3b8c-4c70-ba70-566b96178c87': 'Archived'
};

export const Layout = () => {
  const { advisoryOrganizationId, planId } = Route.useParams();
  const plan = useGetPlanSuspense(planId);
  const org = useGetAdminAdvisoryOrganization(advisoryOrganizationId);
  const program = useGetAdminPlanManagedAccountProgram(planId);
  const equityWashRules = useGetAdminPlanEquityWashRules(planId);
  const navigate = useNavigate({ from: Route.fullPath });
  const { pathname } = useLocation();
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isDemoResetOpen, toggleDemoResetOpen] = useToggle();
  const [isMapReanalysisOpen, toggleMapReanalysisOpen] = useToggle();
  const [isParticipantLiteSyncOpen, toggleParticipantLiteSyncOpen] =
    useToggle();
  const [isParticipantSyncOpen, toggleParticipantSyncOpen] = useToggle();
  const [isPlanSyncOpen, togglePlanSyncOpen] = useToggle();
  const [isStatusOpen, toggleStatusOpen] = useToggle();
  const [isToggleActOpen, toggleToggleActOpen] = useToggle();
  const [isToggleDirectAnnuityOpen, toggleToggleDirectAnnuityOpen] =
    useToggle();

  const ssnResetAll = usePostAdminSsnResetAll({
    mutation: {
      onError: () => notifications.error('Failed to run demo reset'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminParticipantsQueryKey()
        });
        notifications.success('Demo reset completed successfully');
      }
    }
  });

  const participantLiteSync = usePostAdminParticipantLiteSync({
    mutation: {
      onError: () =>
        notifications.error('Failed to trigger participant lite sync'),
      onSuccess: () =>
        notifications.success('Participant lite sync triggered successfully')
    }
  });

  const participantSync = usePostAdminPlanReliusParticipantSync({
    mutation: {
      onError: () => notifications.error('Failed to trigger participant sync'),
      onSuccess: () =>
        notifications.success('Participant sync triggered successfully')
    }
  });

  const planSync = usePostAdminPlanReliusPlanSync({
    mutation: {
      onError: () => notifications.error('Failed to trigger plan sync'),
      onSuccess: () => notifications.success('Plan sync triggered successfully')
    }
  });

  const mapReanalysis = usePostAdminPlanMapReanalysis({
    mutation: {
      onError: () => notifications.error('Failed to trigger MAP reanalysis'),
      onSuccess: data => {
        if (!data.queuedCount) {
          notifications.warning(
            'No eligible participants found for MAP reanalysis'
          );
          return;
        }

        notifications.success(
          `MAP reanalysis queued for ${data.queuedCount} participants`
        );
      }
    }
  });

  const invalidatePlanQueries = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey.some(
          key => typeof key === 'string' && key.includes(planId)
        )
    });
  };

  const patchStatus = usePatchAdminPlanStatus({
    mutation: {
      onError: () => notifications.error('Failed to update plan status'),
      onSuccess: () => {
        invalidatePlanQueries();
        notifications.success('Plan status updated');
      }
    }
  });

  const patchPlan = usePatchPlan({
    mutation: {
      onError: () => notifications.error('Failed to update plan'),
      onSuccess: () => {
        invalidatePlanQueries();
        notifications.success('Plan updated');
      }
    }
  });

  const deletePlan = useDeletePlan({
    mutation: {
      onError: () => notifications.error('Failed to delete plan'),
      onSuccess: () => {
        invalidatePlanQueries();
        queryClient.invalidateQueries({
          queryKey: [
            `/api/v3/admin/advisory-organizations/${advisoryOrganizationId}/plans`
          ]
        });
        notifications.success('Plan deletion initiated');
        navigate({
          params: { advisoryOrganizationId },
          to: '/organizations/$advisoryOrganizationId/plans'
        });
      }
    }
  });

  const basePath = `/organizations/${advisoryOrganizationId}/plans/${planId}`;
  const tabValue = pathname.startsWith(`${basePath}/advisors`)
    ? 'advisors'
    : pathname.startsWith(`${basePath}/funds`)
      ? 'funds'
      : pathname.startsWith(`${basePath}/managed-account-program`)
        ? 'managed-account-program'
        : pathname.startsWith(`${basePath}/core-calcs`)
          ? 'core-calcs'
          : pathname.startsWith(`${basePath}/settings`)
            ? 'settings'
            : pathname.startsWith(`${basePath}/equity-wash`)
              ? 'equity-wash'
              : 'participants';

  const checkIJoinEnabled = usePostAdminPlanReliusCheckIJoinEnabled({
    mutation: {
      onError: () => notifications.error('Failed to check PLANVOICE'),
      onSuccess: data => {
        invalidatePlanQueries();

        if (data.reactivated)
          notifications.success('Plan reactivated via PLANVOICE');
      }
    }
  });

  const onNavigateParticipants = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/participants'
    });

  const onNavigateAdvisors = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/advisors'
    });

  const onNavigateFunds = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/funds'
    });

  const onNavigateManagedAccountProgram = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/managed-account-program'
    });

  const onNavigateCoreCalcs = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/core-calcs'
    });

  const onNavigateSettings = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/settings'
    });

  const onNavigateEquityWash = () =>
    navigate({
      to: '/organizations/$advisoryOrganizationId/plans/$planId/equity-wash'
    });

  const onConfirmToggleDirectAnnuity = () => {
    toggleToggleDirectAnnuityOpen();
    patchPlan.mutate({
      data: { isDirectAnnuity: !plan.data.isDirectAnnuity },
      planId
    });
  };

  const onConfirmReactivate = () => {
    toggleStatusOpen();
    patchStatus.mutate({
      data: { planStatusId: PLAN_STATUS.LIVE },
      planId
    });
  };

  const onConfirmArchive = () => {
    toggleStatusOpen();
    patchStatus.mutate({
      data: { planStatusId: PLAN_STATUS.ARCHIVE },
      planId
    });
  };

  const onConfirmToggleAct = () => {
    toggleToggleActOpen();
    patchPlan.mutate({
      data: { isAnalyticPortalAct: !plan.data.isAnalyticPortalAct },
      planId
    });
  };

  const onConfirmDemoReset = () => {
    toggleDemoResetOpen();
    ssnResetAll.mutate({ planId });
  };

  const onConfirmPlanSync = () => {
    togglePlanSyncOpen();
    planSync.mutate({ planId });
  };

  const onConfirmParticipantSync = () => {
    toggleParticipantSyncOpen();
    participantSync.mutate({ planId });
  };

  const onConfirmParticipantLiteSync = () => {
    toggleParticipantLiteSyncOpen();
    participantLiteSync.mutate({ planId });
  };

  const onConfirmDelete = () => {
    toggleDeleteOpen();
    deletePlan.mutate({ planId });
  };

  const onCheckIJoinEnabled = () => checkIJoinEnabled.mutate({ planId });

  const onCopyPlanId = () => {
    copyToClipboard(planId);
    notifications.success('Plan ID copied');
  };

  const onCopyPlanCorrelationId = () => {
    copyToClipboard(plan.data.planCorrelationId);
    notifications.success('Plan Correlation ID copied');
  };

  const onCopyWhiteLabelId = () => {
    copyToClipboard(plan.data.whiteLabelId);
    notifications.success('Whitelabel ID copied');
  };

  const onConfirmMapReanalysis = () => {
    toggleMapReanalysisOpen();
    mapReanalysis.mutate({ planId });
  };

  const isArchived = plan.data.planStatusId === PLAN_STATUS.ARCHIVE;
  const isDeleting = plan.data.planStatusId === PLAN_STATUS.DELETING;
  const statusLabel = PLAN_STATUS_LABELS[plan.data.planStatusId] ?? 'Unknown';

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Link
        params={{ advisoryOrganizationId }}
        style={{ alignItems: 'center', display: 'flex', gap: 4 }}
        to='/organizations/$advisoryOrganizationId/plans'>
        <ArrowBackIcon />
        {org.data?.name ?? 'Advisory Organization'}
      </Link>

      <Card sx={{ p: 1 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack
              alignItems='flex-start'
              direction='row'
              justifyContent='space-between'
              spacing={2}>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Plan
                </Typography>
                <Typography variant='h5'>
                  {plan.data.description}
                  {plan.data.externalPlanId && ` [${plan.data.externalPlanId}]`}
                </Typography>
              </div>
              <ActionMenu
                actions={[
                  {
                    content: 'Copy Plan ID',
                    onSelect: onCopyPlanId
                  },
                  {
                    content: 'Copy Plan Correlation ID',
                    onSelect: onCopyPlanCorrelationId
                  },
                  {
                    content: 'Copy Whitelabel ID',
                    onSelect: onCopyWhiteLabelId
                  },
                  {
                    content: isArchived ? 'Reactivate Plan' : 'Archive Plan',
                    onSelect: toggleStatusOpen
                  },
                  { content: 'Delete Plan', onSelect: toggleDeleteOpen },
                  { content: 'Demo Reset', onSelect: toggleDemoResetOpen },
                  {
                    content: plan.data.isAnalyticPortalAct
                      ? 'Disable ACT'
                      : 'Enable ACT',
                    onSelect: toggleToggleActOpen
                  },
                  plan.data.planOffersAnnuity && {
                    content: plan.data.isDirectAnnuity
                      ? 'Disable Direct Annuity'
                      : 'Enable Direct Annuity',
                    onSelect: toggleToggleDirectAnnuityOpen
                  },
                  plan.data.isRelius && {
                    content: 'Plan Sync',
                    onSelect: togglePlanSyncOpen
                  },
                  plan.data.isRelius && {
                    content: 'Participant Sync',
                    onSelect: toggleParticipantSyncOpen
                  },
                  plan.data.isRelius && {
                    content: 'Participant Lite Sync',
                    onSelect: toggleParticipantLiteSyncOpen
                  },
                  plan.data.planStatusId === PLAN_STATUS.LIVE &&
                    program.data?.programStatus === 'active' && {
                      content: 'Trigger MAP Reanalysis',
                      disabled: mapReanalysis.isPending,
                      onSelect: toggleMapReanalysisOpen,
                      tooltip: mapReanalysis.isPending
                        ? 'MAP reanalysis is already in progress'
                        : undefined
                    }
                ]
                  .filter(action => !!action)
                  .sort((a, b) => a.content.localeCompare(b.content))}
                trigger={
                  <IconButton data-testid='planActionsMenuButton'>
                    <MoreVertIcon />
                  </IconButton>
                }
              />
            </Stack>
            <Stack
              direction='row'
              spacing={4}
              sx={{ flexWrap: 'wrap', rowGap: 2 }}>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Status
                </Typography>
                <div>
                  <StatusBadge
                    severity={
                      isDeleting ? 'error' : isArchived ? 'warning' : 'success'
                    }>
                    {statusLabel}
                  </StatusBadge>
                </div>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  ACT Analytics
                </Typography>
                <div>
                  <StatusBadge
                    severity={
                      plan.data.isAnalyticPortalAct ? 'success' : 'info'
                    }>
                    {plan.data.isAnalyticPortalAct ? 'Enabled' : 'Disabled'}
                  </StatusBadge>
                </div>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Auto Force Out
                </Typography>
                <div>
                  <StatusBadge
                    severity={
                      plan.data.enableAutoForceOut ? 'success' : 'info'
                    }>
                    {plan.data.enableAutoForceOut ? 'Enabled' : 'Disabled'}
                  </StatusBadge>
                </div>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Direct Annuity
                </Typography>
                <div>
                  <StatusBadge
                    severity={plan.data.isDirectAnnuity ? 'success' : 'info'}>
                    {plan.data.isDirectAnnuity ? 'Enabled' : 'Disabled'}
                  </StatusBadge>
                </div>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Last Sync
                </Typography>
                <Typography sx={{ fontFamily: 'monospace' }} variant='body2'>
                  {plan.data.lastSync
                    ? formatDate(plan.data.lastSync, {
                        format: 'MM/DD/YYYY h:mm A'
                      })
                    : ''}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Subdomain
                </Typography>
                <Stack alignItems='center' direction='row' spacing={0.5}>
                  <Typography sx={{ fontFamily: 'monospace' }} variant='body2'>
                    {plan.data.whitelabelSubdomain ?? ''}
                  </Typography>
                  {plan.data.whitelabelSubdomain && (
                    <CopyableText value={plan.data.whitelabelSubdomain} />
                  )}
                </Stack>
              </div>
            </Stack>
          </Stack>
        </CardContent>
        {isArchived ? (
          <ConfirmationDialog
            confirmText='Reactivate'
            data-testid='reactivatePlan'
            isOpen={isStatusOpen}
            message={`This will set "${plan.data.description}" back to Live status.`}
            onCancel={toggleStatusOpen}
            onConfirm={onConfirmReactivate}
            title='Reactivate Plan'
          />
        ) : (
          <ConfirmationDialog
            confirmColor='error'
            confirmText='Archive'
            data-testid='archivePlan'
            isOpen={isStatusOpen}
            message={`This will archive "${plan.data.description}". The plan will no longer be active.`}
            onCancel={toggleStatusOpen}
            onConfirm={onConfirmArchive}
            title='Archive Plan'
          />
        )}
        <ConfirmationDialog
          confirmText={plan.data.isAnalyticPortalAct ? 'Disable' : 'Enable'}
          data-testid='toggleAct'
          isOpen={isToggleActOpen}
          message={
            plan.data.isAnalyticPortalAct
              ? `This will disable ACT Analytics on "${plan.data.description}".`
              : `This will enable ACT Analytics on "${plan.data.description}".`
          }
          onCancel={toggleToggleActOpen}
          onConfirm={onConfirmToggleAct}
          title={plan.data.isAnalyticPortalAct ? 'Disable ACT' : 'Enable ACT'}
        />
        <ConfirmationDialog
          confirmText={plan.data.isDirectAnnuity ? 'Disable' : 'Enable'}
          data-testid='toggleDirectAnnuity'
          isOpen={isToggleDirectAnnuityOpen}
          message={
            plan.data.isDirectAnnuity
              ? `This will disable Direct Annuity allocations on "${plan.data.description}".`
              : `This will enable Direct Annuity allocations on "${plan.data.description}".`
          }
          onCancel={toggleToggleDirectAnnuityOpen}
          onConfirm={onConfirmToggleDirectAnnuity}
          title={
            plan.data.isDirectAnnuity
              ? 'Disable Direct Annuity'
              : 'Enable Direct Annuity'
          }
        />
        <ConfirmationDialog
          confirmColor='error'
          confirmText='Reset'
          data-testid='demoResetAll'
          isOpen={isDemoResetOpen}
          message='This will unfinalize all plan enrollments, delete export queue entries, and delete allocations for all participants within this plan.'
          onCancel={toggleDemoResetOpen}
          onConfirm={onConfirmDemoReset}
          title='Demo Reset'
        />
        {plan.data.isRelius && (
          <>
            <ConfirmationDialog
              confirmText='Sync'
              data-testid='planSync'
              isOpen={isPlanSyncOpen}
              message={`This will import the plan configuration for "${plan.data.description}" from Relius.`}
              onCancel={togglePlanSyncOpen}
              onConfirm={onConfirmPlanSync}
              title='Plan Sync'
            />
            <ConfirmationDialog
              confirmText='Sync'
              data-testid='participantSync'
              isOpen={isParticipantSyncOpen}
              message='This will run a full participant sync from Relius for all participants in this plan.'
              onCancel={toggleParticipantSyncOpen}
              onConfirm={onConfirmParticipantSync}
              title='Participant Sync'
            />
            <ConfirmationDialog
              confirmText='Sync'
              data-testid='participantLiteSync'
              isOpen={isParticipantLiteSyncOpen}
              message='This will trigger a participant lite sync for all participants in this plan.'
              onCancel={toggleParticipantLiteSyncOpen}
              onConfirm={onConfirmParticipantLiteSync}
              title='Participant Lite Sync'
            />
          </>
        )}
        <ConfirmationDialog
          confirmText='Trigger'
          data-testid='mapReanalysis'
          isOpen={isMapReanalysisOpen}
          message={`This will trigger MAP reanalysis for all eligible participants in "${plan.data.description}". Their managed account allocations will be recalculated.`}
          onCancel={toggleMapReanalysisOpen}
          onConfirm={onConfirmMapReanalysis}
          title='Trigger MAP Reanalysis'
        />
        <ConfirmationDialog
          confirmationText='DELETE'
          confirmColor='error'
          confirmText='Delete'
          data-testid='deletePlan'
          isOpen={isDeleteOpen}
          message={`This will permanently delete "${plan.data.description}" and all associated data. This action is irreversible.`}
          onCancel={toggleDeleteOpen}
          onConfirm={onConfirmDelete}
          title='Delete Plan'
        />
        {plan.data.isRelius && (
          <>
            <Divider />
            <CardContent>
              <Stack alignItems='center' direction='row' spacing={2}>
                <Typography color='text.secondary' variant='overline'>
                  iJoin Enabled
                </Typography>
                <Button
                  disabled={checkIJoinEnabled.isPending}
                  onClick={onCheckIJoinEnabled}
                  variant='outlined'>
                  {checkIJoinEnabled.isPending ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  Check
                </Button>
                {checkIJoinEnabled.data && (
                  <Stack alignItems='center' direction='row' spacing={2}>
                    {checkIJoinEnabled.data.reactivated && (
                      <Typography color='success.main' variant='body2'>
                        Reactivated
                      </Typography>
                    )}
                    <Stack alignItems='center' direction='row' spacing={0.5}>
                      {checkIJoinEnabled.data.enableIjoinCd === 'Y' ? (
                        <CheckCircleIcon color='success' />
                      ) : (
                        <CancelIcon color='error' />
                      )}
                      <Typography variant='body2'>
                        ENABLEIJOINCD:{' '}
                        {checkIJoinEnabled.data.enableIjoinCd ?? ''}
                      </Typography>
                    </Stack>
                    <Stack alignItems='center' direction='row' spacing={0.5}>
                      {checkIJoinEnabled.data.statCd === 'W' ||
                      checkIJoinEnabled.data.statCd === 'B' ? (
                        <CheckCircleIcon color='success' />
                      ) : (
                        <CancelIcon color='error' />
                      )}
                      <Typography variant='body2'>
                        STATCD: {checkIJoinEnabled.data.statCd ?? ''}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </>
        )}
      </Card>

      <Tabs scrollButtons='auto' value={tabValue} variant='scrollable'>
        <Tab
          data-testid='tabParticipants'
          label='Participants'
          onClick={onNavigateParticipants}
          value='participants'
        />
        <Tab
          data-testid='tabAdvisors'
          label='Advisors'
          onClick={onNavigateAdvisors}
          value='advisors'
        />
        <Tab
          data-testid='tabFunds'
          label='Funds'
          onClick={onNavigateFunds}
          value='funds'
        />
        {program.data != null && (
          <Tab
            data-testid='tabManagedAccountProgram'
            label='Managed Account Program'
            onClick={onNavigateManagedAccountProgram}
            value='managed-account-program'
          />
        )}
        <Tab
          data-testid='tabCoreCalcs'
          label='Core Calcs'
          onClick={onNavigateCoreCalcs}
          value='core-calcs'
        />
        <Tab
          data-testid='tabSettings'
          label='Settings'
          onClick={onNavigateSettings}
          value='settings'
        />
        {!!equityWashRules.data?.length && (
          <Tab
            data-testid='tabEquityWash'
            label='Equity Wash'
            onClick={onNavigateEquityWash}
            value='equity-wash'
          />
        )}
      </Tabs>

      <Outlet />
    </Stack>
  );
};

export const Route = createFileRoute(
  '/organizations/$advisoryOrganizationId/plans/$planId'
)({
  component: () => (
    <QueryBoundary>
      <Layout />
    </QueryBoundary>
  )
});
